// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for retrieving asset lifecycle events.
 * Provides functionality to fetch all events associated with an asset,
 * returning them in chronological order for timeline display and analysis.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { eventSchema } from '@/asset/api/types';
import { compareEvents } from '@/asset/utilities/compare-events';

/**
 * Schema for validating the API response when fetching events.
 * Transforms the response to provide a more intuitive structure with
 * the events array separated from metadata. Events are automatically
 * sorted chronologically using the compareEvents utility.
 */
const eventsResponseSchema = z
  .object({
    data: z.array(eventSchema).nullable(),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    events: data?.slice().sort(compareEvents) ?? [],
  }));

type EventsResponse = z.infer<typeof eventsResponseSchema>;

/**
 * Retrieves all events in an asset's lifecycle.
 * This function handles the API call to fetch the complete event history
 * for an asset and validates the response data using Zod schemas.
 * The events are automatically sorted chronologically for consistent
 * timeline presentation.
 *
 * @param assetId - Unique identifier of the asset to fetch events for
 * @returns Promise resolving to the sorted list of events and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function getEvents(assetId: string): Promise<EventsResponse> {
  const url = `/v1/asset/${assetId}/event`;
  const response = await api.get(url);
  const result = await parseResponse(eventsResponseSchema, response.data, {
    schemaName: 'AssetEvents',
    endpoint: url,
  });

  return result;
}
